// frontend/src/features/workspace/components/ProjectSelectorDialog.tsx
import { useState, useEffect, type JSX } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useProjects, useCreateProject } from '../api/use-projects'
import { useCreateModel } from '../api/use-models'
import { logger } from '@/utils/logger'
import { useTrackEvent, type TrackedImportSourceType } from '@/lib/pendo-events'
import type { SourceType } from '@/features/import/types/source-type'
import { Sparkles, AlertTriangle } from 'lucide-react'

interface ProjectSelectorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  system: unknown
  /**
   * `SourceType` (the real import-format union) plus `'manual'`, which callers
   * pass when the model was built from scratch or a template rather than
   * imported — see the note on `SourceType` itself for why `'manual'` isn't
   * folded into that union. Narrowed from a bare `string` in #786: that
   * widening was hiding the fact that `'manual'` didn't actually belong to
   * the exported union, not a deliberate design choice.
   */
  sourceType: SourceType | 'manual'
  defaultModelName?: string
  importJobId?: string
  /**
   * Invoked after the create-model POST resolves. Receives the new IDs +
   * version AND the same `system` payload that was just sent to the server,
   * so callers can seed it into their local store before navigating to the
   * editor route. Without this, the editor renders the previously-loaded
   * model until the next round-trip refresh — see fix in NewModelFlow.
   */
  onModelCreated: (projectId: string, modelId: string, version: number, system: unknown) => void
  /** AI generation summary text */
  aiSummary?: string | undefined
  /** AI generation warnings/assumptions */
  aiWarnings?: string[] | undefined
  /**
   * Provided ONLY when the grid basis is still a choice — i.e. a model being
   * created from scratch. Absent for imports and templates, where the basis is
   * whatever the source declared and changing it would be a destructive
   * coordinate transform (see newDocumentHeader). Its presence is what flips the
   * Grid field between a toggle and a locked readout: the dialog cannot "allow
   * editing" a basis it has no way to write back.
   */
  onBasisChange?: (basis: 'orthogonal' | 'isometric') => void
}

/**
 * Whether `sourceType` is one of the values `Import Completed` is allowed to
 * report (`TrackedImportSourceType`, in lib/pendo-events.ts).
 *
 * This dialog's `sourceType` prop is `SourceType | 'manual'` — every value
 * UploadPage/NewModelFlow can pass it — but the Pendo event is typed more
 * tightly on purpose: it excludes `revit-ifc`, `revit-schedule`, and `dxf`
 * (hidden from the picker but still wired) and `'manual'` (not an import).
 *
 * CAUTION — adding a `return true` case here for one of those hidden values
 * (e.g. `case 'revit-ifc':`) is NOT sufficient and NOT type-checked.
 * TypeScript trusts this function's `sourceType is TrackedImportSourceType`
 * annotation; it never verifies that annotation against what the switch body
 * actually returns. Only widening `TrackedImportSourceType` itself (in
 * lib/pendo-events.ts) is caught at compile time — that's what the
 * `@ts-expect-error` coverage in persist.pendo.test.tsx pins. So tracking a
 * newly re-enabled source type is a two-file edit: add the case here AND add
 * the value to `TrackedImportSourceType`. Do the second without the first and
 * `trackEvent` below silently sends nothing for it; do the first without the
 * second and it silently sends the new value with no compiler complaint.
 */
function isTrackedImportSource(sourceType: SourceType | 'manual'): sourceType is TrackedImportSourceType {
  switch (sourceType) {
    case 'pipe-flo':
    case 'json':
    case 'pipe-flo-desktop-json':
    case 'pipe-flo-docs':
    case 'aft-fathom':
      return true
    default:
      return false
  }
}

/** Read the grid basis out of a pre-built (unknown-typed) system for display. */
function basisOf(system: unknown): 'orthogonal' | 'isometric' {
  const gridType = (system as { document_header?: { grid_settings?: { grid_type?: string } } })
    ?.document_header?.grid_settings?.grid_type
  return gridType === 'isometric' ? 'isometric' : 'orthogonal'
}

export function ProjectSelectorDialog({
  open,
  onOpenChange,
  system,
  sourceType,
  defaultModelName,
  importJobId,
  onModelCreated,
  aiSummary,
  aiWarnings,
  onBasisChange,
}: ProjectSelectorDialogProps): JSX.Element {
  const basis = basisOf(system)
  const { data: projects, isLoading: loadingProjects } = useProjects()
  const createProject = useCreateProject()
  const createModel = useCreateModel()
  const trackEvent = useTrackEvent()

  const [selectedProjectId, setSelectedProjectId] = useState<string>('__new__')
  const [newProjectName, setNewProjectName] = useState('')
  const [newProjectDesc, setNewProjectDesc] = useState('')
  const [modelName, setModelName] = useState(defaultModelName ?? '')
  const [submitting, setSubmitting] = useState(false)

  // When projects load and there are existing ones, default to the first
  useEffect((): void => {
    if (projects?.length && selectedProjectId === '__new__') {
      setSelectedProjectId(projects[0]!.id)
    }
  }, [projects, selectedProjectId])

  // Update model name when default changes
  useEffect((): void => {
    if (defaultModelName) setModelName(defaultModelName)
  }, [defaultModelName])

  const isCreatingProject = selectedProjectId === '__new__'

  const handleSubmit = async (): Promise<void> => {
    if (!modelName.trim()) return
    setSubmitting(true)

    try {
      let projectId = selectedProjectId

      // Create new project if needed
      if (isCreatingProject) {
        if (!newProjectName.trim()) return
        const desc = newProjectDesc.trim()
        const project = await createProject.mutateAsync({
          name: newProjectName.trim(),
          ...(desc ? { description: desc } : {}),
        })
        projectId = project.id
      }

      // Create model
      const result = await createModel.mutateAsync({
        projectId,
        name: modelName.trim(),
        sourceType,
        system,
        ...(importJobId ? { importJobId } : {}),
      })

      if (isTrackedImportSource(sourceType)) {
        trackEvent('Import Completed', { sourceType })
      }
      onModelCreated(projectId, result.id, result.version, system)
    } catch (err) {
      logger.error('Failed to save model', err instanceof Error ? err : undefined, {
        selectedProjectId,
        isCreatingProject,
        modelName: modelName.trim(),
        sourceType,
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save Model</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* AI generation summary */}
          {aiSummary && (
            <div className="rounded-md border border-primary/20 bg-primary/5 p-3 space-y-2">
              <div className="flex items-start gap-2 text-sm">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{aiSummary}</span>
              </div>
              {aiWarnings && aiWarnings.length > 0 && (
                <div className="space-y-1 pt-1">
                  {aiWarnings.map((warning, i): JSX.Element => (
                    <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                      <span>{warning}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Project selector */}
          <div className="space-y-2">
            <Label>Project</Label>
            {loadingProjects ? (
              <div className="text-sm text-muted-foreground">Loading projects...</div>
            ) : (
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects?.map((p): JSX.Element => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="__new__">+ Create New Project</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {/* New project fields (shown when "Create New" selected) */}
          {isCreatingProject && (
            <div className="space-y-3 rounded-md border p-3">
              <div className="space-y-2">
                <Label>Project Name</Label>
                <Input
                  placeholder="e.g., Plant A, Client XYZ"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Input
                  placeholder="Brief description"
                  value={newProjectDesc}
                  onChange={(e) => setNewProjectDesc(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Model name */}
          <div className="space-y-2">
            <Label>Model Name</Label>
            <Input
              placeholder="e.g., Feed Water System"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
          </div>

          {/* Grid basis. A creation-time choice (toggle) for new models; a locked
              readout for imports/templates, whose basis is fixed by the source. */}
          <div className="space-y-2">
            <Label>Grid</Label>
            {onBasisChange ? (
              <div className="grid grid-cols-2 gap-2">
                {(['orthogonal', 'isometric'] as const).map((b): JSX.Element => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => onBasisChange(b)}
                    className={
                      'rounded-md border px-3 py-2 text-sm capitalize transition-colors ' +
                      (basis === b
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-input text-muted-foreground hover:bg-accent')
                    }
                  >
                    {b}
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-md border border-input bg-muted/40 px-3 py-2 text-sm capitalize text-muted-foreground">
                {basis}
              </div>
            )}
            {basis === 'isometric' && (
              <p className="text-xs text-muted-foreground">
                Devices snap to the isometric grid. Pipes between them run isometric
                only when their devices sit on a shared axis.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              submitting ||
              !modelName.trim() ||
              (isCreatingProject && !newProjectName.trim())
            }
          >
            {submitting ? 'Saving...' : 'Open in Editor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
